import { useState } from 'react'
import { DateTime, Duration } from 'luxon'

import { useInterval } from 'src/lib/hooks/use-interval'
import { PendingMatchStatus } from 'src/graphql/types'

const interval = Duration.fromObject({ seconds: 1 }).as('milliseconds')

// Fill climbs linearly to the halfway mark, then eases off exponentially toward
// a cap it never quite reaches — the ring only closes when the match actually
// completes, so a slow match parks near the cap instead of lying at 100%.
// halfTime ~ a third of the prod median (~156s); cap leaves a small top gap.
const halfTime = Duration.fromObject({ seconds: 50 }).as('milliseconds')
const cap = 0.96
const half = 0.5
// derivative-matched at halfTime so linear -> exponential joins without a kink
const tau = (halfTime * (cap - half)) / half

// After motionTimeout a still-pending card stops animating (probably stuck);
// after stalledTimeout it reads as stalled (red) and the modal offers support.
export const motionTimeout = Duration.fromObject({ minutes: 6 }).as(
  'milliseconds'
)
export const stalledTimeout = Duration.fromObject({ minutes: 10 }).as(
  'milliseconds'
)

const doneStatuses = [PendingMatchStatus.Completed, PendingMatchStatus.Failed]

export interface ProcessingState {
  progress: number
  motionPaused: boolean
  stalled: boolean
}

const doneState: ProcessingState = {
  progress: 1,
  motionPaused: true,
  stalled: false
}

function elapsed(createdAt: string) {
  return Math.abs(
    DateTime.fromISO(createdAt).diffNow('milliseconds').milliseconds
  )
}

function fillAt(t: number) {
  if (t <= halfTime) return half * (t / halfTime)
  return cap - (cap - half) * Math.exp(-(t - halfTime) / tau)
}

// Time is clamped to motionTimeout, so a frozen card parks just under the cap.
// Derived from createdAt (not from 0) so a refresh resumes at the right point
// instead of restarting.
function computeState(createdAt: string): ProcessingState {
  const t = elapsed(createdAt)
  return {
    progress: fillAt(Math.min(t, motionTimeout)),
    motionPaused: t > motionTimeout,
    stalled: t > stalledTimeout
  }
}

function sameState(a: ProcessingState, b: ProcessingState) {
  return (
    a.progress === b.progress &&
    a.motionPaused === b.motionPaused &&
    a.stalled === b.stalled
  )
}

// Progress, motion-pause and stalled for one pending card from a single 1s
// interval; re-renders stop once the values settle (frozen, then stalled).
export function useProcessing(
  createdAt: string,
  status: PendingMatchStatus
): ProcessingState {
  const isDone = doneStatuses.includes(status)
  const [state, setState] = useState(() =>
    isDone ? doneState : computeState(createdAt)
  )

  useInterval(() => {
    if (isDone) return
    setState((prev) => {
      const next = computeState(createdAt)
      return sameState(prev, next) ? prev : next
    })
  }, interval)

  return isDone ? doneState : state
}

// Standalone stalled check for consumers that only need it (status, modal).
export function useStalled(createdAt: string) {
  const [stalled, setStalled] = useState(
    () => elapsed(createdAt) > stalledTimeout
  )

  useInterval(() => {
    if (stalled) return
    if (elapsed(createdAt) > stalledTimeout) setStalled(true)
  }, interval)

  return stalled
}
